import { SxProps, Table, TableBody, TableCell, TableHead, TableRow, Theme } from '@mui/material';
import { useTranslation } from '@shared/utils/i18n';
import { CombinedActItem } from '@shared/types/osi/acts';
import { formatActAmount } from '@widgets/osiActs/ui/combined/format';

interface Props {
  items: CombinedActItem[];
  /** id текущего дома — его строка выделяется. */
  currentOsiId: number | null;
}

/**
 * OSI-818: подсветка строки текущего дома в той же геометрии, что и шапка из темы
 * (MuiTableCell.head: скругление theme.shape.borderRadius + inset-тень 8px по краям).
 * Радиус — строкой с px: числовое значение в sx для радиусов умножилось бы на shape.borderRadius.
 */
const currentRowSx: SxProps<Theme> = (theme) => {
  const radius = `${theme.shape.borderRadius}px`;

  return {
    '& > td': { backgroundColor: theme.palette.action.selected },
    '& > td:first-of-type': {
      borderTopLeftRadius: radius,
      borderBottomLeftRadius: radius,
      boxShadow: `inset 8px 0 0 ${theme.palette.background.paper}`
    },
    '& > td:last-of-type': {
      borderTopRightRadius: radius,
      borderBottomRightRadius: radius,
      boxShadow: `inset -8px 0 0 ${theme.palette.background.paper}`
    }
  };
};

/**
 * OSI-818: состав общего акта — по строке на акт каждого дома группы.
 * Используется и в блоке общего акта на странице, и в диалоге подписания,
 * чтобы подписант видел, какие именно дома он подписывает.
 */
export const CombinedActItemsTable: React.FC<Props> = ({ items, currentOsiId }) => {
  const { t } = useTranslation();

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{t('acts:combinedAct.house')}</TableCell>
          <TableCell>{t('acts:combinedAct.actNumber')}</TableCell>
          <TableCell align="right">{t('acts:combinedAct.amount')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((item) => {
          const isCurrent = item.osiId === currentOsiId;

          return (
            // OSI-818: подсветка текущего дома рисуется на ЯЧЕЙКАХ, а не на строке (selected).
            // Шапку тема (shared/theme/overrides/Table.js, MuiTableCell.head) рисует заливкой
            // ячеек со скруглением и inset-тенью 8px цвета фона по краям — визуально она уже
            // таблицы. Фон строки Mui-selected шёл на всю ширину без скругления и вылезал за
            // края шапки. Повторяем для ячеек строки те же фон, скругление и тень.
            <TableRow key={item.actId} aria-selected={isCurrent} sx={isCurrent ? currentRowSx : undefined}>
              <TableCell sx={{ fontWeight: isCurrent ? 'bold' : undefined }}>
                {item.osiAddress || item.osiName || item.osiId}
                {isCurrent && ` (${t('acts:combinedAct.currentHouse')})`}
              </TableCell>
              <TableCell>{item.actNum}</TableCell>
              <TableCell align="right">{formatActAmount(item.amount)}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
